import { SITE } from "@/lib/site";
import { useGo } from "@/lib/nav";
import { Instagram, Facebook } from "lucide-react";
import { PatternLayer, patternFor } from "./patterns";

export const Footer = () => {
  const go = useGo();
  const linkClass = "text-neutral-400 hover:text-white transition-colors duration-300";

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-[#0D0D0D] text-neutral-300 border-t border-[#C9A227]/25">
      <PatternLayer pattern={patternFor("footer")} />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-10">
        <div data-testid="footer-col-brand">
          <img src="/assets/logo.png" alt="Gold Time — Fine Jewelry and Watches" className="h-9 w-auto" data-testid="footer-logo" />
          <p className="mt-4 text-sm leading-relaxed text-neutral-400 max-w-xs">
            A trusted Texas family business since 1991 — buying and selling gold, Swiss watches, and diamond
            jewelry honestly, and in front of you.
          </p>
          <p className="mt-3 text-xs uppercase tracking-[0.15em] text-[#D4AF37]/80">Katy · Copperas Cove · Killeen</p>
          <div className="mt-5 flex items-center gap-3">
            <a
              href={SITE.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-social-instagram"
              aria-label="Gold Time on Instagram"
              className="w-10 h-10 rounded-full border border-[#C9A227]/40 flex items-center justify-center text-[#E8CC6E] hover:bg-[#C9A227] hover:text-neutral-900 hover:-translate-y-0.5 transition-[background-color,color,transform] duration-300"
            >
              <Instagram size={17} strokeWidth={1.5} />
            </a>
            <a
              href={SITE.socials.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-social-facebook"
              aria-label="Gold Time on Facebook"
              className="w-10 h-10 rounded-full border border-[#C9A227]/40 flex items-center justify-center text-[#E8CC6E] hover:bg-[#C9A227] hover:text-neutral-900 hover:-translate-y-0.5 transition-[background-color,color,transform] duration-300"
            >
              <Facebook size={17} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        <div data-testid="footer-col-shop">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Shop</p>
          <div className="mt-4 flex flex-col gap-3 text-sm">
            <a href="/catalog" onClick={go("/catalog")} data-testid="footer-link-catalog" className={linkClass}>Catalog</a>
            <a href="/collections" onClick={go("/collections")} data-testid="footer-link-collections-page" className={linkClass}>Collections</a>
            <a href="/collections/jewelry-under-500" onClick={go("/collections/jewelry-under-500")} data-testid="footer-link-under-500" className={linkClass}>Gifts Under $500</a>
            <a href="/collections/lab-grown-diamonds" onClick={go("/collections/lab-grown-diamonds")} data-testid="footer-link-lab-diamonds-shop" className={linkClass}>Lab-Grown Diamonds</a>
            <a href="/collections/loose-stones" onClick={go("/collections/loose-stones")} data-testid="footer-link-loose-stones" className={linkClass}>Loose Stones</a>
            <a href="/collections/nameplates" onClick={go("/collections/nameplates")} data-testid="footer-link-nameplates" className={linkClass}>Custom Nameplates</a>
            <a href="/catalog?category=Chains" onClick={go("/catalog?category=Chains")} data-testid="footer-link-gold-chains" className={linkClass}>Gold Chains</a>
            <a href="/catalog?category=Rings" onClick={go("/catalog?category=Rings")} data-testid="footer-link-diamond-rings" className={linkClass}>Diamond Rings</a>
          </div>
        </div>

        <div data-testid="footer-col-guides">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Guides</p>
          <div className="mt-4 flex flex-col gap-3 text-sm">
            <a href="/lab-grown-diamonds" onClick={go("/lab-grown-diamonds")} data-testid="footer-link-lab-grown" className={linkClass}>Lab-Grown Diamond Guide</a>
            <a href="/diamond-buying-guide" onClick={go("/diamond-buying-guide")} data-testid="footer-link-diamond-guide" className={linkClass}>Diamond Buying Guide</a>
            <a href="/gold-karat-guide" onClick={go("/gold-karat-guide")} data-testid="footer-link-gold-guide" className={linkClass}>Gold Karat Guide</a>
            <a href="/pre-owned-watch-guide" onClick={go("/pre-owned-watch-guide")} data-testid="footer-link-watch-guide" className={linkClass}>Pre-Owned Watch Guide</a>
            <a href="/ring-size-guide" onClick={go("/ring-size-guide")} data-testid="footer-link-ring-guide" className={linkClass}>Ring Size Guide</a>
            <a href="/financing" onClick={go("/financing")} data-testid="footer-link-financing" className={linkClass}>Financing Options</a>
            <a href="/warranty" onClick={go("/warranty")} data-testid="footer-link-warranty" className={linkClass}>Warranty &amp; Authenticity</a>
          </div>
        </div>

        <div data-testid="footer-col-sell">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Sell &amp; Exchange</p>
          <div className="mt-4 flex flex-col gap-3 text-sm">
            <a href="/sell" onClick={go("/sell")} data-testid="footer-link-sell" className={linkClass}>Sell to Gold Time</a>
            <a href="/sell-gold" onClick={go("/sell-gold")} data-testid="footer-link-sell-gold" className={linkClass}>Sell Your Gold</a>
            <a href="/sell-diamonds" onClick={go("/sell-diamonds")} data-testid="footer-link-sell-diamonds" className={linkClass}>Sell Diamonds</a>
            <a href={SITE.watchBuyersUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-link-watch-buyers" className={linkClass}>
              Sell a Swiss Watch — {SITE.watchBuyersName}
            </a>
            <a href="/showrooms" onClick={go("/showrooms")} data-testid="footer-link-locations" className={linkClass}>Showrooms</a>
            <a href="/contact" onClick={go("/contact")} data-testid="footer-link-contact" className={linkClass}>Contact</a>
          </div>
        </div>

        <div data-testid="footer-col-visit">
          <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Visit</p>
          <div className="mt-4 flex flex-col gap-3 text-sm">
            {SITE.locations.map((loc, i) => (
              <a key={loc.name} href={loc.phoneHref} data-testid={`footer-phone-${i}`} className={linkClass}>
                <span className="text-neutral-500">{loc.name.replace(" Showroom", "")}:</span> {loc.phone}
              </a>
            ))}
            <a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className={linkClass}>WhatsApp</a>
            <span className="text-neutral-500" data-testid="footer-hours">Mon–Sat 10am–7pm &middot; Sun 12–6pm</span>
            <a href={`mailto:${SITE.emails.main}`} data-testid="footer-email" className={linkClass}>{SITE.emails.main}</a>
            <span className="text-neutral-500">{SITE.domain}</span>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-6 border-t border-neutral-800 text-xs text-neutral-500 flex flex-wrap items-center justify-between gap-3">
        <span>&copy; {new Date().getFullYear()} Gold Time Jewelry. All rights reserved.</span>
        <span className="flex gap-5">
          <a href="/terms" onClick={go("/terms")} data-testid="footer-link-terms" className="hover:text-white transition-colors duration-300">Terms &amp; Conditions</a>
          <a href="/privacy" onClick={go("/privacy")} data-testid="footer-link-privacy" className="hover:text-white transition-colors duration-300">Privacy Policy</a>
          <a href="/refund-policy" onClick={go("/refund-policy")} data-testid="footer-link-refund" className="hover:text-white transition-colors duration-300">Refund Policy</a>
        </span>
      </div>
    </footer>
  );
};
